import type { EmployeeCalendarDay } from "@/lib/employeeCalendar";
import { api } from "@/lib/apiClient";

export type AbsenceType =
  | "urlaub"
  | "krank"
  | "kindkrank"
  | "ueberstundenabbau"
  | "sonderurlaub"
  | "schulung"
  | "unbezahlt"
  | "nicht_verfuegbar";

export type AbsenceStatus = "pending" | "approved" | "rejected" | "cancelled" | "acknowledged";

export type HalfDayPart = "am" | "pm";

export interface EmployeeAbsenceRequest {
  id: number;
  user_login: string;
  user_display: string | null;
  absence_type: AbsenceType;
  date_from: string;
  date_to: string;
  day_fraction: number;
  half_day_part: HalfDayPart | null;
  status: AbsenceStatus;
  note: string | null;
  total_days: number;
  requested_at: string;
  requested_by_login: string | null;
  reviewed_by_login: string | null;
  reviewed_by_display: string | null;
  reviewed_at: string | null;
  review_comment: string | null;
}

export const ABSENCE_TYPE_OPTIONS: Array<{ value: AbsenceType; label: string }> = [
  { value: "urlaub", label: "Urlaub" },
  { value: "krank", label: "Krank" },
  { value: "kindkrank", label: "Kindkrank" },
  { value: "ueberstundenabbau", label: "Überstundenabbau" },
  { value: "sonderurlaub", label: "Sonderurlaub" },
  { value: "schulung", label: "Schulung / Weiterbildung" },
  { value: "unbezahlt", label: "Unbezahlt" },
];

export const ABSENCE_STATUS_LABELS: Record<AbsenceStatus, string> = {
  pending: "Ausstehend",
  approved: "Genehmigt",
  rejected: "Abgelehnt",
  cancelled: "Zurückgezogen",
  acknowledged: "Gelesen",
};

const ABSENCE_TYPE_LABELS: Record<AbsenceType, string> = {
  urlaub: "Urlaub",
  krank: "Krank",
  kindkrank: "Kindkrank",
  ueberstundenabbau: "Überstundenabbau",
  sonderurlaub: "Sonderurlaub",
  schulung: "Schulung / Weiterbildung",
  unbezahlt: "Unbezahlt",
  nicht_verfuegbar: "Nicht verfügbar",
};

/** Ausstehende Krankheit/Kindkrank ist im Montageplan auch für Monteure sichtbar. */
export const PUBLIC_PENDING_ABSENCE_TYPES: ReadonlySet<AbsenceType> = new Set([
  "krank",
  "kindkrank",
]);

export function isPublicPendingAbsenceType(type: AbsenceType | string): boolean {
  return PUBLIC_PENDING_ABSENCE_TYPES.has(type as AbsenceType);
}

/** Nachunternehmer-Hinweis: schraffiert im Montageplan, keine Freigabe. */
export function isUnavailabilityNotice(type: AbsenceType | string): boolean {
  return type === "nicht_verfuegbar";
}

export function isAutoApprovedWithoutReview(req: EmployeeAbsenceRequest): boolean {
  return (
    req.status === "approved" &&
    req.absence_type === "ueberstundenabbau" &&
    (req.review_comment || "").toLowerCase().includes("keine freigabe")
  );
}

function buildQuery(params: Record<string, string | number | boolean | undefined | null>): string {
  const parts: string[] = [];
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
  }
  return parts.length ? `?${parts.join("&")}` : "";
}

export function getAbsenceTypeLabel(type: AbsenceType): string {
  return ABSENCE_TYPE_LABELS[type] ?? type;
}

export function formatAbsenceDateRange(req: EmployeeAbsenceRequest): string {
  const from = formatDateDe(req.date_from);
  const to = formatDateDe(req.date_to);
  if (req.date_from === req.date_to) {
    if (Number(req.day_fraction) === 0.5) {
      const part = req.half_day_part === "am" ? "vormittags" : "nachmittags";
      return `${from} (${part})`;
    }
    return from;
  }
  return `${from} – ${to}`;
}

export function formatDateDe(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split("-");
  if (!y || !m || !d) return iso;
  return `${d}.${m}.${y}`;
}

export function formatAbsenceDays(totalDays: number): string {
  const n = Number(totalDays);
  if (!Number.isFinite(n)) return "—";
  if (n === 1) return "1 Tag";
  if (n === 0.5) return "0,5 Tage";
  return `${n.toLocaleString("de-DE", { maximumFractionDigits: 2 })} Tage`;
}

export function listAbsenceRequests(params?: {
  user_login?: string;
  status?: AbsenceStatus;
  year?: number;
}): Promise<EmployeeAbsenceRequest[]> {
  return api.get(`/employee-times/absences${buildQuery(params ?? {})}`);
}

/** Hero-Abwesenheiten (Urlaub, Krank, …) für die Team-Kachel, gleicher Stand wie der Montageplan. */
export function listHeroTeamAbsences(params: {
  date_from: string;
  date_to: string;
}): Promise<EmployeeAbsenceRequest[]> {
  return api.get(`/employee-times/absences/hero-team${buildQuery(params)}`);
}

export function createAbsenceRequest(payload: {
  absence_type: AbsenceType;
  date_from: string;
  date_to: string;
  day_fraction?: number;
  half_day_part?: HalfDayPart | null;
  note?: string;
  user_login?: string;
  user_display?: string;
}): Promise<EmployeeAbsenceRequest> {
  return api.post("/employee-times/absences", payload);
}

export function cancelAbsenceRequest(id: number): Promise<EmployeeAbsenceRequest> {
  return api.post(`/employee-times/absences/${id}/cancel`, {});
}

/** Büro-Löschen genehmigter/freigegebener Abwesenheiten – Begründung Pflicht. */
export function deleteAbsenceRequest(
  id: number,
  reason: string,
): Promise<EmployeeAbsenceRequest> {
  return api.post(`/employee-times/absences/${id}/office-delete`, { reason });
}

export function listPendingAbsenceRequests(): Promise<EmployeeAbsenceRequest[]> {
  return api.get("/employee-times/absences/pending");
}

export function approveAbsenceRequest(
  id: number,
  comment?: string,
): Promise<EmployeeAbsenceRequest> {
  return api.post(`/employee-times/absences/${id}/approve`, { comment });
}

export function rejectAbsenceRequest(
  id: number,
  comment?: string,
): Promise<EmployeeAbsenceRequest> {
  return api.post(`/employee-times/absences/${id}/reject`, { comment });
}

export function acknowledgeAbsenceRequest(id: number): Promise<EmployeeAbsenceRequest> {
  return api.post(`/employee-times/absences/${id}/acknowledge`, {});
}

export interface EmployeeAbsenceBudget {
  user_login: string;
  user_display: string | null;
  year: number;
  vacation_days: number;
  /** Wirksamer Übertrag (manuell oder automatisch aus Resturlaub Vorjahr). */
  vacation_carryover_days: number;
  /** True = manuell gesetzt (auch 0); False = automatisch. */
  vacation_carryover_manual?: boolean;
  /** Vorschlag aus Resturlaub des Vorjahres. */
  vacation_carryover_auto_days?: number;
  total_vacation_budget: number;
  child_sick_days: number;
  overtime_reduction_days: number;
  special_leave_days: number;
  used_vacation_days: number;
  used_child_sick_days: number;
  used_overtime_reduction_days: number;
  used_special_leave_days: number;
  used_sick_days: number;
  used_unpaid_days: number;
  used_training_days: number;
  pending_vacation_days: number;
  remaining_vacation_days: number;
  available_vacation_days: number;
  remaining_child_sick_days: number;
  remaining_overtime_reduction_days: number;
  remaining_special_leave_days: number;
  note: string | null;
  updated_at: string | null;
  updated_by_login: string | null;
}

export const ABSENCE_SHORT_CODES: Record<AbsenceType, string> = {
  urlaub: "U",
  krank: "K",
  kindkrank: "KK",
  ueberstundenabbau: "ÜA",
  sonderurlaub: "SU",
  schulung: "SW",
  unbezahlt: "UB",
  nicht_verfuegbar: "NV",
};

export interface AbsenceDayMarker {
  date: string;
  absence_type: AbsenceType;
  status: AbsenceStatus;
  day_fraction: number;
  half_day_part: HalfDayPart | null;
}

function parseIsoDate(iso: string): Date {
  const parts = iso.slice(0, 10).split("-").map(Number);
  const y = parts[0] ?? 2000;
  const m = parts[1] ?? 1;
  const d = parts[2] ?? 1;
  return new Date(y, m - 1, d);
}

function formatIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function addDays(d: Date, count: number): Date {
  const next = new Date(d);
  next.setDate(next.getDate() + count);
  return next;
}

export function expandAbsenceRequestsToDayMap(
  requests: EmployeeAbsenceRequest[],
  options: { year: number; month?: number; includePending?: boolean },
): Map<string, AbsenceDayMarker[]> {
  const map = new Map<string, AbsenceDayMarker[]>();
  for (const req of requests) {
    if (req.status === "rejected" || req.status === "cancelled") continue;
    if (req.status === "pending" && !options.includePending) continue;

    let current = parseIsoDate(req.date_from);
    const end = parseIsoDate(req.date_to);
    while (current <= end) {
      const iso = formatIsoDate(current);
      const [y, m] = iso.split("-").map(Number);
      if (y !== options.year) {
        current = addDays(current, 1);
        continue;
      }
      if (options.month != null && m !== options.month) {
        current = addDays(current, 1);
        continue;
      }

      const markers = map.get(iso) ?? [];
      markers.push({
        date: iso,
        absence_type: req.absence_type,
        status: req.status,
        day_fraction: Number(req.day_fraction),
        half_day_part: req.half_day_part,
      });
      map.set(iso, markers);
      current = addDays(current, 1);
    }
  }
  return map;
}

function isAutoApprovedCalendarAbsenceDay(day: EmployeeCalendarDay): boolean {
  if ((day.kind !== "bridge" && day.kind !== "betriebsferien") || day.works_instead) {
    return false;
  }
  if (day.is_bridge_default) return Boolean(day.effective_absence_type);
  return (
    !day.is_override &&
    !day.absence_request_id &&
    day.absence_request_status === "approved" &&
    Boolean(day.effective_absence_type)
  );
}

export function mergeBridgeDefaultsIntoDayMap(
  map: Map<string, AbsenceDayMarker[]>,
  calendarByDay: Map<string, EmployeeCalendarDay>,
  options: { year: number; month?: number },
): Map<string, AbsenceDayMarker[]> {
  const result = new Map(map);
  for (const [iso, day] of calendarByDay) {
    if (!isAutoApprovedCalendarAbsenceDay(day) || !day.effective_absence_type) continue;

    const [y, m] = iso.split("-").map(Number);
    if (y !== options.year) continue;
    if (options.month != null && m !== options.month) continue;
    if ((result.get(iso)?.length ?? 0) > 0) continue;

    const fraction = day.day_fraction ?? 1;
    result.set(iso, [
      {
        date: iso,
        absence_type: day.effective_absence_type,
        status: "approved",
        day_fraction: fraction < 1 ? 0.5 : 1,
        half_day_part: fraction < 1 ? day.half_day_part ?? "pm" : null,
      },
    ]);
  }
  return result;
}

export function expandEffectiveAbsenceDays(
  requests: EmployeeAbsenceRequest[],
  calendarByDay: Map<string, EmployeeCalendarDay>,
  options: { year: number; month?: number; includePending?: boolean },
): Map<string, AbsenceDayMarker[]> {
  const fromRequests = expandAbsenceRequestsToDayMap(requests, options);
  return mergeBridgeDefaultsIntoDayMap(fromRequests, calendarByDay, options);
}

export function isHalfDayAbsence(abs: {
  day_fraction?: number | string | null;
}): boolean {
  return Number(abs.day_fraction) === 0.5;
}

export function formatAbsenceHalfDayPart(
  part: HalfDayPart | null | undefined,
  style: "short" | "long" = "short",
): string {
  if (style === "long") {
    return part === "am" ? "vormittags" : "nachmittags";
  }
  return part === "am" ? "VM" : "NM";
}

/** Kürzel für Kalender und Montageplan, z. B. `U½ VM`. */
export function formatAbsenceBoardLabel(abs: {
  absence_type: AbsenceType;
  day_fraction?: number | string | null;
  half_day_part?: HalfDayPart | null;
}): string {
  const code = ABSENCE_SHORT_CODES[abs.absence_type];
  if (isHalfDayAbsence(abs)) {
    return `${code}½ ${formatAbsenceHalfDayPart(abs.half_day_part)}`;
  }
  return code;
}

export function formatAbsenceMarkerLabel(marker: AbsenceDayMarker): string {
  return formatAbsenceBoardLabel(marker);
}

export function absenceMarkerClassName(marker: AbsenceDayMarker): string {
  const pending = marker.status === "pending";
  const base = pending ? "border border-dashed opacity-80 " : "";
  switch (marker.absence_type) {
    case "urlaub":
      return `${base}bg-sky-500/15 text-sky-700 dark:text-sky-300`;
    case "krank":
      return `${base}bg-rose-500/15 text-rose-700 dark:text-rose-300`;
    case "kindkrank":
      return `${base}bg-orange-500/15 text-orange-700 dark:text-orange-300`;
    case "ueberstundenabbau":
      return `${base}bg-violet-500/15 text-violet-700 dark:text-violet-300`;
    case "sonderurlaub":
      return `${base}bg-teal-500/15 text-teal-700 dark:text-teal-300`;
    case "schulung":
      return `${base}bg-emerald-500/15 text-emerald-700 dark:text-emerald-300`;
    default:
      return `${base}bg-fill-tertiary text-label-secondary`;
  }
}

export function listAbsenceBudgets(params: {
  year: number;
  user_login?: string;
}): Promise<EmployeeAbsenceBudget[]> {
  return api.get(`/employee-times/absence-budgets${buildQuery(params)}`, {
    timeoutMs: params.user_login ? 30_000 : 60_000,
  });
}

export function upsertAbsenceBudget(payload: {
  user_login: string;
  user_display?: string;
  year: number;
  vacation_days: number;
  vacation_carryover_days?: number | null;
  vacation_carryover_manual?: boolean;
  child_sick_days?: number;
  overtime_reduction_days?: number;
  special_leave_days?: number;
  note?: string;
}): Promise<EmployeeAbsenceBudget> {
  return api.put("/employee-times/absence-budgets", payload);
}

export function formatBudgetDays(value: number): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("de-DE", { maximumFractionDigits: 2 });
}

/** Maximaler Kalender-Zeitraum für Urlaub (Von inkl. bis Von+21 = 3 Wochen). */
export const MAX_VACATION_CALENDAR_SPAN_DAYS = 21;

function parseIsoDateParts(iso: string): { y: number; m: number; d: number } {
  const parts = iso.slice(0, 10).split("-");
  const y = Number(parts[0]);
  const m = Number(parts[1]);
  const d = Number(parts[2]);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) {
    throw new Error(`Ungültiges ISO-Datum: ${iso}`);
  }
  return { y, m, d };
}

export function addDaysIso(iso: string, days: number): string {
  const { y, m, d } = parseIsoDateParts(iso);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
}

/** ISO-Wochentag (1=Mo … 7=So) eines ISO-Datums. */
function isoWeekdayOfIso(iso: string): number {
  const { y, m, d } = parseIsoDateParts(iso);
  const jsDay = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return jsDay === 0 ? 7 : jsDay;
}

function isWeekendIso(iso: string): boolean {
  const weekday = isoWeekdayOfIso(iso);
  return weekday >= 6;
}

/**
 * Anrechenbarer Abwesenheitstag: kein Wochenende, kein Feiertag und – falls
 * bekannt – ein Arbeitstag laut Arbeitszeitmodell (`workWeekdays`, ISO 1–7).
 * Ohne `workWeekdays` gilt Mo–Fr.
 */
export function isChargeableAbsenceDayIso(
  iso: string,
  holidayDates?: ReadonlySet<string>,
  workWeekdays?: ReadonlySet<number> | readonly number[] | null,
): boolean {
  if (isWeekendIso(iso)) return false;
  if (holidayDates?.has(iso.slice(0, 10))) return false;
  if (workWeekdays) {
    const weekday = isoWeekdayOfIso(iso);
    const set = workWeekdays instanceof Set ? workWeekdays : new Set(workWeekdays);
    if (set.size > 0 && !set.has(weekday)) return false;
  }
  return true;
}

/** Letztes erlaubtes Bis-Datum für Urlaub: min(Von+3 Wochen, Restanspruch je Kalenderjahr). */
export function maxBisDateForVacation(params: {
  dateFrom: string;
  availableVacationDays: number;
  /**
   * Verfügbare Tage je Kalenderjahr. Ein Tag verbraucht nur das Kontingent seines Jahres.
   * Fehlt ein Jahr, stoppt das Kontingent dort nicht (noch nicht geladen); es gilt der 3-Wochen-Deckel.
   */
  availableVacationDaysByYear?: Readonly<Record<number, number>>;
  holidayDates?: ReadonlySet<string>;
  /** Arbeitstage laut Arbeitszeitmodell (ISO 1–7); ohne Angabe Mo–Fr. */
  workWeekdays?: ReadonlySet<number> | readonly number[] | null;
}): string | null {
  const dateFrom = params.dateFrom.slice(0, 10);
  if (!dateFrom) return null;
  const byYear = params.availableVacationDaysByYear;
  const fallback = Number(params.availableVacationDays);
  if (!byYear && (!Number.isFinite(fallback) || fallback <= 0)) return null;

  const calendarCap = addDaysIso(dateFrom, MAX_VACATION_CALENDAR_SPAN_DAYS);
  const usedByYear = new Map<number, number>();
  let usedFallback = 0;
  let lastOk: string | null = null;
  let current = dateFrom;

  const capFor = (iso: string): number | undefined => {
    if (byYear) {
      const year = Number(iso.slice(0, 4));
      if (!Object.prototype.hasOwnProperty.call(byYear, year)) return undefined;
      const value = Number(byYear[year]);
      return Number.isFinite(value) ? value : 0;
    }
    return Number.isFinite(fallback) ? fallback : 0;
  };

  while (current <= calendarCap) {
    if (isChargeableAbsenceDayIso(current, params.holidayDates, params.workWeekdays)) {
      const cap = capFor(current);
      if (cap !== undefined) {
        if (byYear) {
          const year = Number(current.slice(0, 4));
          const used = usedByYear.get(year) ?? 0;
          if (used + 1 > cap) break;
          usedByYear.set(year, used + 1);
        } else if (usedFallback + 1 > cap) {
          break;
        } else {
          usedFallback += 1;
        }
      }
    }
    lastOk = current;
    current = addDaysIso(current, 1);
  }

  const startCap = capFor(dateFrom);
  if (
    lastOk == null &&
    startCap !== undefined &&
    startCap >= 0.5 &&
    isChargeableAbsenceDayIso(dateFrom, params.holidayDates, params.workWeekdays)
  ) {
    return dateFrom;
  }
  return lastOk;
}

/** Verfügbare Überstundenabbau-Tage aus Stundenstand und bereits reservierten Anträgen. */
export function availableOvertimeReductionDays(params: {
  overtimeBalanceHours: number | null | undefined;
  averageDailySollHours: number | null | undefined;
  reservedDays?: number;
}): number {
  const balance = Number(params.overtimeBalanceHours);
  const daily = Number(params.averageDailySollHours);
  if (!Number.isFinite(balance) || !Number.isFinite(daily) || daily <= 0) return 0;
  const reserved = Number(params.reservedDays) || 0;
  const raw = balance / daily - reserved;
  if (!Number.isFinite(raw) || raw <= 0) return 0;
  return Math.floor(raw * 2) / 2;
}

export function reservedOvertimeReductionDays(
  requests: EmployeeAbsenceRequest[],
  overtimeAsOf?: string | null,
): number {
  const asOf = (overtimeAsOf || "").slice(0, 10);
  let reserved = 0;
  for (const req of requests) {
    if (req.absence_type !== "ueberstundenabbau") continue;
    if (req.status !== "pending" && req.status !== "approved") continue;
    const total = Number(req.total_days);
    if (!Number.isFinite(total) || total <= 0) continue;
    if (req.status === "pending") {
      reserved += total;
      continue;
    }
    if (!asOf || req.date_to.slice(0, 10) <= asOf) continue;
    // Genehmigt und ganz/teilweise nach dem Kontostand: konservativ volle Tage zählen.
    reserved += total;
  }
  return reserved;
}

/** Letztes erlaubtes Bis-Datum für Überstundenabbau anhand verfügbarer Tage. */
export function maxBisDateForOvertimeReduction(params: {
  dateFrom: string;
  availableDays: number;
  holidayDates?: ReadonlySet<string>;
  /** Arbeitstage laut Arbeitszeitmodell (ISO 1–7); ohne Angabe Mo–Fr. */
  workWeekdays?: ReadonlySet<number> | readonly number[] | null;
}): string | null {
  const dateFrom = params.dateFrom.slice(0, 10);
  if (!dateFrom) return null;
  const available = Number(params.availableDays);
  if (!Number.isFinite(available) || available <= 0) return null;

  // Großzügiger Kalender-Puffer (Wochenenden/Feiertage), begrenzt durch verfügbare Tage.
  const calendarCap = addDaysIso(dateFrom, Math.max(30, Math.ceil(available) * 3));
  let used = 0;
  let lastOk: string | null = null;
  let current = dateFrom;

  while (current <= calendarCap) {
    if (isChargeableAbsenceDayIso(current, params.holidayDates, params.workWeekdays)) {
      if (used + 1 > available) break;
      used += 1;
    }
    lastOk = current;
    current = addDaysIso(current, 1);
  }

  if (
    lastOk == null &&
    available >= 0.5 &&
    isChargeableAbsenceDayIso(dateFrom, params.holidayDates, params.workWeekdays)
  ) {
    return dateFrom;
  }
  return lastOk;
}
